import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Users, UserPlus, Search, Shield, ShieldOff,
  Clock, CheckCircle, XCircle, ChevronLeft, ChevronRight,
  RefreshCw, Key, Calendar, Mail, MoreVertical, X, Trash2
} from 'lucide-react';
import { getToken, ACCESS_LABELS } from '../../services/authService';
import { fetchAllCohorts, fetchUserCohorts, assignUserCohort, removeUserCohort, type Cohort } from '../../services/cohortService';

// ─── Types ──────────────────────────────────────────────────────
interface AdminUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  phone: string | null;
  country: string | null;
  city: string | null;
  is_active: boolean;
  is_admin: boolean;
  email_verified: boolean;
  last_login: string | null;
  login_count: number;
  created_at: string;
  profile_image_url: string | null;
  subscription_id: number | null;
  subscription_status: string | null;
  plan_name: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  has_access: number;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  total_pages: number;
}

// ─── API Base ───────────────────────────────────────────────────
const ADMIN_API = process.env.REACT_APP_AUTH_API
  ? process.env.REACT_APP_AUTH_API.replace('/auth', '/admin')
  : (process.env.NODE_ENV === 'production' ? '/api/admin' : 'http://localhost:8080/api/admin');

function authHeaders() {
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${getToken()}`
  };
}

// ─── Component ──────────────────────────────────────────────────
const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [pagination, setPagination] = useState<Pagination>({ page: 1, limit: 20, total: 0, total_pages: 0 });
  const [search, setSearch] = useState('');
  const [filterAccess, setFilterAccess] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAccessModal, setShowAccessModal] = useState<AdminUser | null>(null);
  const [showResetPwModal, setShowResetPwModal] = useState<AdminUser | null>(null);
  const [showCohortModal, setShowCohortModal] = useState<AdminUser | null>(null);
  const [activeMenu, setActiveMenu] = useState<number | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  // Toast
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // ─── Fetch Users ────────────────────────────────────────────
  const fetchUsers = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (search) params.set('search', search);
      if (filterAccess) params.set('has_access', filterAccess);

      const res = await fetch(`${ADMIN_API}/users.php?${params}`, { headers: authHeaders() });
      const data = await res.json();

      if (data.success) {
        setUsers(data.users);
        setPagination(data.pagination);
      } else {
        showToast(data.error || 'Error cargando usuarios', 'error');
      }
    } catch {
      showToast('Error de conexión al servidor', 'error');
    } finally {
      setLoading(false);
    }
  }, [search, filterAccess]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // ─── Actions ────────────────────────────────────────────────
  const toggleActive = async (user: AdminUser) => {
    setActionLoading(user.id);
    try {
      const res = await fetch(`${ADMIN_API}/users.php?id=${user.id}`, { method: 'DELETE', headers: authHeaders() });
      const data = await res.json();
      if (data.success) {
        showToast(data.message);
        fetchUsers(pagination.page);
      } else {
        showToast(data.error || 'Error', 'error');
      }
    } catch {
      showToast('Error de conexión', 'error');
    } finally {
      setActionLoading(null);
      setActiveMenu(null);
    }
  };

  const deleteUser = async (user: AdminUser) => {
    if (!window.confirm(`¿Estás seguro de ELIMINAR DEFINITIVAMENTE a ${user.email}?\n\nEsta acción no se puede deshacer. Se borrarán todos sus datos, suscripciones e historial.`)) {
      return;
    }
    setActionLoading(user.id);
    try {
      const res = await fetch(`${ADMIN_API}/users.php?id=${user.id}&action=delete`, { method: 'DELETE', headers: authHeaders() });
      const data = await res.json();
      if (data.success) {
        showToast(data.message);
        fetchUsers(pagination.page);
      } else {
        showToast(data.error || 'Error', 'error');
      }
    } catch {
      showToast('Error de conexión', 'error');
    } finally {
      setActionLoading(null);
      setActiveMenu(null);
    }
  };

  const grantAccess = async (userId: number, months: number) => {
    setActionLoading(userId);
    try {
      const res = await fetch(`${ADMIN_API}/subscriptions.php`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ user_id: userId, months })
      });
      const data = await res.json();

      if (data.success) {
        showToast(data.message);
        setShowAccessModal(null);
        fetchUsers(pagination.page);
      } else if (data.error?.includes('ya tiene')) {
        // Ya tiene, intentar extender
        const resExt = await fetch(`${ADMIN_API}/subscriptions.php`, {
          method: 'PUT',
          headers: authHeaders(),
          body: JSON.stringify({ user_id: userId, action: 'extend', months })
        });
        const dataExt = await resExt.json();
        showToast(dataExt.success ? dataExt.message : (dataExt.error || 'Error'), dataExt.success ? 'success' : 'error');
        setShowAccessModal(null);
        fetchUsers(pagination.page);
      } else {
        showToast(data.error || 'Error', 'error');
      }
    } catch {
      showToast('Error de conexión', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const revokeAccess = async (userId: number) => {
    setActionLoading(userId);
    try {
      const res = await fetch(`${ADMIN_API}/subscriptions.php?user_id=${userId}`, { method: 'DELETE', headers: authHeaders() });
      const data = await res.json();
      showToast(data.success ? data.message : (data.error || 'Error'), data.success ? 'success' : 'error');
      fetchUsers(pagination.page);
    } catch {
      showToast('Error de conexión', 'error');
    } finally {
      setActionLoading(null);
      setActiveMenu(null);
    }
  };

  const adminPut = async (url: string, body: object) => {
    // Try PUT first; fall back to POST+_method=PUT for Apache CGI hosts
    let res = await fetch(url, { method: 'PUT', headers: authHeaders(), body: JSON.stringify(body) });
    if (res.status === 400) {
      const peek = await res.clone().json().catch(() => null);
      if (peek?.error?.includes('Body vacío') || peek?.error?.includes('campos')) {
        res = await fetch(`${url}${url.includes('?') ? '&' : '?'}_method=PUT`, {
          method: 'POST', headers: authHeaders(), body: JSON.stringify(body),
        });
      }
    }
    return res;
  };

  const updateAccessLevel = async (userId: number, level: number) => {
    const current = users.find(u => u.id === userId);
    if (current && current.has_access === level) return;

    setActionLoading(userId);
    try {
      const res = await adminPut(`${ADMIN_API}/users.php?id=${userId}`, { access_level: level });
      const data = await res.json();
      if (data.success) {
        showToast(`Acceso actualizado: ${ACCESS_LABELS[level]}`);
        fetchUsers(pagination.page);
      } else {
        showToast(data.error || 'Error', 'error');
      }
    } catch {
      showToast('Error de conexion', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const resetPassword = async (userId: number, newPassword: string, sendEmail: boolean) => {
    setActionLoading(userId);
    try {
      const res = await adminPut(`${ADMIN_API}/users.php?id=${userId}`, { new_password: newPassword, send_email: sendEmail });
      const data = await res.json();
      showToast(data.success ? data.message : (data.error || 'Error'), data.success ? 'success' : 'error');
      setShowResetPwModal(null);
    } catch {
      showToast('Error de conexión', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const setExpiry = async (userId: number, date: string) => {
    setActionLoading(userId);
    try {
      const res = await adminPut(`${ADMIN_API}/subscriptions.php`, {
        user_id: userId, action: 'set_expiry', expiry_date: date,
      });
      const data = await res.json();
      showToast(data.success ? data.message : (data.error || 'Error'), data.success ? 'success' : 'error');
      fetchUsers(pagination.page);
    } catch {
      showToast('Error de conexión', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  // ─── Helpers ────────────────────────────────────────────────
  const formatDate = (d: string | null) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const daysUntilExpiry = (d: string | null) => {
    if (!d) return null;
    const diff = Math.ceil((new Date(d).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    return diff;
  };

  // ─── Render ─────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-[60] px-4 py-3 rounded-lg shadow-lg text-white text-sm font-medium flex items-center gap-2 transition-all ${
          toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
        }`}>
          {toast.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/admin" className="text-gray-500 hover:text-gray-700 transition-colors" title="Volver al panel">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-600" />
                <h1 className="text-lg font-semibold text-gray-900">Gestión de Usuarios</h1>
              </div>
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                Admin
              </span>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
            >
              <UserPlus className="w-4 h-4" />
              Nuevo usuario
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Stats cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Total</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{pagination.total}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Con acceso</p>
            <p className="text-2xl font-bold text-green-600 mt-1">
              {users.filter(u => u.has_access > 0).length}
              <span className="text-sm text-gray-400 font-normal"> / pag</span>
            </p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Sin acceso</p>
            <p className="text-2xl font-bold text-gray-400 mt-1">
              {users.filter(u => u.has_access === 0).length}
              <span className="text-sm text-gray-400 font-normal"> / pag</span>
            </p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Inactivos</p>
            <p className="text-2xl font-bold text-red-500 mt-1">
              {users.filter(u => !u.is_active).length}
              <span className="text-sm text-gray-400 font-normal"> / pág</span>
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por email, nombre..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchUsers(1)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              />
            </div>
            <select
              value={filterAccess}
              onChange={(e) => setFilterAccess(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white"
            >
              <option value="">Todos</option>
              <option value="1">Con acceso</option>
              <option value="0">Sin acceso</option>
            </select>
            <button
              onClick={() => fetchUsers(1)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Buscar
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-4 py-3 font-medium text-gray-500">Usuario</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500 hidden md:table-cell">Estado</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">Acceso</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500 hidden lg:table-cell">Expira</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500 hidden lg:table-cell">Registro</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500 hidden lg:table-cell">Actividad</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-500">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="text-center py-12 text-gray-500">
                      <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />
                      Cargando...
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-12 text-gray-400">
                      No se encontraron usuarios
                    </td>
                  </tr>
                ) : users.map(user => {
                  const expDays = daysUntilExpiry(user.current_period_end);
                  return (
                    <tr key={user.id} className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors">
                      {/* User info */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                            user.is_admin ? 'bg-purple-500' : user.has_access > 0 ? 'bg-blue-500' : 'bg-gray-400'
                          }`}>
                            {(user.first_name?.[0] || user.email[0]).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 flex items-center gap-1.5">
                              {user.full_name || user.email.split('@')[0]}
                              {user.is_admin && (
                                <span className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium">ADMIN</span>
                              )}
                            </p>
                            <p className="text-xs text-gray-500">{user.email}</p>
                          </div>
                        </div>
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                          user.is_active ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                        }`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${user.is_active ? 'bg-green-500' : 'bg-red-500'}`} />
                          {user.is_active ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>

                      {/* Access */}
                      <td className="px-4 py-3">
                        <select
                          value={user.has_access}
                          onChange={(e) => updateAccessLevel(user.id, Number(e.target.value))}
                          disabled={actionLoading === user.id}
                          className={`text-xs font-medium rounded-lg border px-2 py-1 outline-none cursor-pointer transition-colors ${
                            user.has_access === 7 ? 'bg-purple-50 border-purple-200 text-purple-700' :
                            user.has_access > 0 ? 'bg-emerald-50 border-emerald-200 text-emerald-700' :
                            'bg-gray-50 border-gray-200 text-gray-500'
                          } disabled:opacity-50`}
                        >
                          {Object.entries(ACCESS_LABELS).map(([val, label]) => (
                            <option key={val} value={val}>{label}</option>
                          ))}
                        </select>
                      </td>

                      {/* Expiry — clickeable para editar */}
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <div className="group relative">
                          <input
                            type="date"
                            value={user.current_period_end ? user.current_period_end.slice(0, 10) : ''}
                            onChange={(e) => { if (e.target.value) setExpiry(user.id, e.target.value); }}
                            disabled={actionLoading === user.id}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                          />
                          {user.current_period_end ? (
                            <span className={`text-xs cursor-pointer group-hover:text-blue-600 group-hover:underline transition-colors ${
                              expDays !== null && expDays <= 7 ? 'text-orange-600 font-medium' : 'text-gray-600'
                            }`}>
                              {formatDate(user.current_period_end)}
                              {expDays !== null && expDays <= 7 && expDays > 0 && (
                                <span className="block text-[10px] text-orange-500">({expDays}d restantes)</span>
                              )}
                              {expDays !== null && expDays <= 0 && (
                                <span className="block text-[10px] text-red-500">Expirado</span>
                              )}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400 cursor-pointer group-hover:text-blue-600 group-hover:underline transition-colors">
                              Setear fecha
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Registration date */}
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <span className="text-xs text-gray-500">
                          {formatDate(user.created_at)}
                        </span>
                      </td>

                      {/* Activity: last login + login count */}
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <span className="text-xs text-gray-500">
                          {user.last_login ? formatDate(user.last_login) : 'Nunca'}
                        </span>
                        {user.login_count > 0 && (
                          <span className="block text-[10px] text-gray-400">{user.login_count} {user.login_count === 1 ? 'login' : 'logins'}</span>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3 text-right">
                        <div className="relative inline-block">
                          <button
                            onClick={(e) => {
                              if (activeMenu === user.id) { setActiveMenu(null); return; }
                              const rect = e.currentTarget.getBoundingClientRect();
                              setMenuPos({ top: rect.bottom + 4, left: rect.right - 208 });
                              setActiveMenu(user.id);
                            }}
                            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            <MoreVertical className="w-4 h-4 text-gray-500" />
                          </button>

                          {activeMenu === user.id && (
                            <div className="fixed w-52 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50" style={{ top: menuPos.top, left: menuPos.left }}>
                              <button
                                onClick={() => { setShowAccessModal(user); setActiveMenu(null); }}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2 text-gray-700"
                              >
                                <Calendar className="w-4 h-4 text-blue-500" />
                                {user.has_access > 0 ? 'Extender suscripcion' : 'Otorgar suscripcion'}
                              </button>
                              <button
                                onClick={() => { setShowCohortModal(user); setActiveMenu(null); }}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2 text-gray-700"
                              >
                                <Users className="w-4 h-4 text-emerald-500" />
                                Gestionar camadas
                              </button>
                              {user.has_access > 0 && (
                                <button
                                  onClick={() => revokeAccess(user.id)}
                                  disabled={actionLoading === user.id}
                                  className="w-full text-left px-3 py-2 text-sm hover:bg-red-50 flex items-center gap-2 text-red-600"
                                >
                                  <ShieldOff className="w-4 h-4" />
                                  Revocar suscripcion
                                </button>
                              )}
                              <button
                                onClick={() => { setShowResetPwModal(user); setActiveMenu(null); }}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2 text-gray-700"
                              >
                                <Key className="w-4 h-4 text-amber-500" />
                                Reset password
                              </button>
                              {!user.is_admin && (
                                <>
                                  <hr className="my-1 border-gray-100" />
                                  <button
                                    onClick={() => toggleActive(user)}
                                    disabled={actionLoading === user.id}
                                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2 ${
                                      user.is_active ? 'text-red-600' : 'text-green-600'
                                    }`}
                                  >
                                    {user.is_active ? (
                                      <><XCircle className="w-4 h-4" /> Desactivar cuenta</>
                                    ) : (
                                      <><CheckCircle className="w-4 h-4" /> Activar cuenta</>
                                    )}
                                  </button>
                                  <button
                                    onClick={() => deleteUser(user)}
                                    disabled={actionLoading === user.id}
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-red-50 flex items-center gap-2 text-red-600"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                    Eliminar usuario
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
              <p className="text-xs text-gray-500">
                Mostrando {((pagination.page - 1) * pagination.limit) + 1} – {Math.min(pagination.page * pagination.limit, pagination.total)} de {pagination.total}
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => fetchUsers(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  className="p-1.5 rounded hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm text-gray-600 px-2">
                  {pagination.page} / {pagination.total_pages}
                </span>
                <button
                  onClick={() => fetchUsers(pagination.page + 1)}
                  disabled={pagination.page >= pagination.total_pages}
                  className="p-1.5 rounded hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ─── Create User Modal ───────────────────────────────────── */}
      {showCreateModal && <CreateUserModal onClose={() => setShowCreateModal(false)} onSuccess={() => { setShowCreateModal(false); fetchUsers(1); showToast('Usuario creado exitosamente'); }} />}

      {/* ─── Grant/Extend Access Modal ───────────────────────────── */}
      {showAccessModal && (
        <AccessModal
          user={showAccessModal}
          loading={actionLoading === showAccessModal.id}
          onClose={() => setShowAccessModal(null)}
          onGrant={(months) => grantAccess(showAccessModal.id, months)}
          onSetExpiry={(date) => setExpiry(showAccessModal.id, date)}
        />
      )}

      {/* ─── Reset Password Modal ────────────────────────────────── */}
      {showResetPwModal && (
        <ResetPasswordModal
          user={showResetPwModal}
          loading={actionLoading === showResetPwModal.id}
          onClose={() => setShowResetPwModal(null)}
          onReset={(pw, sendEmail) => resetPassword(showResetPwModal.id, pw, sendEmail)}
        />
      )}

      {/* ─── Cohort Management Modal ────────────────────────────── */}
      {showCohortModal && (
        <CohortModal
          user={showCohortModal}
          onClose={() => setShowCohortModal(null)}
          onUpdate={() => { showToast('Camadas actualizadas'); fetchUsers(pagination.page); }}
        />
      )}

      {/* Click outside to close menus */}
      {activeMenu && <div className="fixed inset-0 z-40" onClick={() => setActiveMenu(null)} />}
    </div>
  );
};

// ─── Create User Modal ─────────────────────────────────────────
function CreateUserModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const [form, setForm] = useState({ email: '', password: '', first_name: '', last_name: '', grant_access: false, access_months: 1, access_level: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${ADMIN_API}/users.php`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (data.success) {
        onSuccess();
      } else {
        setError(data.error || 'Error al crear usuario');
      }
    } catch {
      setError('Error de conexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-blue-600" />
            Nuevo usuario
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Nombre</label>
              <input type="text" value={form.first_name} onChange={e => setForm({...form, first_name: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" placeholder="Franco" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Apellido</label>
              <input type="text" value={form.last_name} onChange={e => setForm({...form, last_name: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" placeholder="Britez" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">Email *</label>
            <input type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" placeholder="usuario@gmail.com" />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">Contrasena *</label>
            <input type="text" required value={form.password} onChange={e => setForm({...form, password: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none font-mono" placeholder="DataEng2026!" />
          </div>

          <div className="bg-gray-50 rounded-lg p-3 space-y-3">
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Nivel de acceso</label>
              <select
                value={form.access_level}
                onChange={e => {
                  const level = Number(e.target.value);
                  setForm({...form, access_level: level, grant_access: level > 0 });
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white"
              >
                {Object.entries(ACCESS_LABELS).map(([val, label]) => (
                  <option key={val} value={val}>{label}</option>
                ))}
              </select>
            </div>
            {form.access_level > 0 && (
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-500">Duracion:</label>
                <select value={form.access_months} onChange={e => setForm({...form, access_months: Number(e.target.value)})} className="px-2 py-1 border border-gray-300 rounded text-sm bg-white">
                  <option value={1}>1 mes</option>
                  <option value={3}>3 meses</option>
                  <option value={6}>6 meses</option>
                  <option value={12}>12 meses</option>
                </select>
              </div>
            )}
          </div>

          <button type="submit" disabled={loading} className="w-full bg-blue-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50">
            {loading ? 'Creando...' : 'Crear usuario'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ─── Access Modal ───────────────────────────────────────────────
function AccessModal({ user, loading, onClose, onGrant, onSetExpiry }: { user: AdminUser; loading: boolean; onClose: () => void; onGrant: (months: number) => void; onSetExpiry: (date: string) => void }) {
  const [months, setMonths] = useState(1);
  const [mode, setMode] = useState<'months' | 'date'>('months');
  const [customDate, setCustomDate] = useState('');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {user.has_access > 0 ? 'Extender suscripcion' : 'Otorgar suscripcion'}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-3">
            <Mail className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-sm font-medium text-gray-900">{user.full_name || 'Sin nombre'}</p>
              <p className="text-xs text-gray-500">{user.email}</p>
              <p className="text-xs text-gray-400 mt-0.5">Acceso actual: {ACCESS_LABELS[user.has_access] || 'Sin acceso'}</p>
            </div>
          </div>
          {user.has_access > 0 && user.current_period_end && (
            <p className="text-xs text-gray-500 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Suscripcion expira: {new Date(user.current_period_end).toLocaleDateString('es-AR')}
            </p>
          )}

          {/* Mode toggle */}
          <div className="flex rounded-lg bg-gray-100 p-0.5">
            <button onClick={() => setMode('months')} className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-colors ${mode === 'months' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}>
              Por meses
            </button>
            <button onClick={() => setMode('date')} className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-colors ${mode === 'date' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}>
              Fecha exacta
            </button>
          </div>

          {mode === 'months' ? (
            <>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-2">Duracion de la suscripcion</label>
                <div className="grid grid-cols-4 gap-2">
                  {[1, 3, 6, 12].map(m => (
                    <button key={m} onClick={() => setMonths(m)} className={`py-2 rounded-lg text-sm font-medium transition-colors ${
                      months === m ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}>
                      {m} {m === 1 ? 'mes' : 'meses'}
                    </button>
                  ))}
                </div>
              </div>
              <button onClick={() => onGrant(months)} disabled={loading} className="w-full bg-blue-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50">
                {loading ? 'Procesando...' : user.has_access > 0 ? `Extender ${months} mes(es)` : `Otorgar ${months} mes(es)`}
              </button>
            </>
          ) : (
            <>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-2">Fecha de expiración</label>
                <input
                  type="date"
                  value={customDate}
                  onChange={e => setCustomDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                />
                {customDate && new Date(customDate) < new Date() && (
                  <p className="text-[10px] text-orange-500 mt-1 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    Fecha en el pasado — el acceso se revocará en el próximo login
                  </p>
                )}
              </div>
              <button
                onClick={() => { if (customDate) { onSetExpiry(customDate); onClose(); } }}
                disabled={loading || !customDate}
                className="w-full bg-blue-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {loading ? 'Procesando...' : `Setear expiración: ${customDate || '—'}`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Reset Password Modal ───────────────────────────────────────
function ResetPasswordModal({ user, loading, onClose, onReset }: { user: AdminUser; loading: boolean; onClose: () => void; onReset: (pw: string, sendEmail: boolean) => void }) {
  const [password, setPassword] = useState('');
  const [sendEmail, setSendEmail] = useState(true);

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$';
    let pw = '';
    for (let i = 0; i < 12; i++) pw += chars[Math.floor(Math.random() * chars.length)];
    setPassword(pw);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Reset contraseña</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-3">
            <Mail className="w-4 h-4 text-gray-400" />
            <p className="text-sm text-gray-700">{user.email}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">Nueva contraseña</label>
            <div className="flex gap-2">
              <input type="text" value={password} onChange={e => setPassword(e.target.value)} className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500 outline-none" placeholder="Nueva contraseña" />
              <button type="button" onClick={generatePassword} className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs font-medium text-gray-600 transition-colors whitespace-nowrap">
                Generar
              </button>
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer bg-gray-50 rounded-lg p-3">
            <input type="checkbox" checked={sendEmail} onChange={e => setSendEmail(e.target.checked)} className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500" />
            <span className="text-sm text-gray-700">Enviar contraseña por email al usuario</span>
          </label>
          <button onClick={() => onReset(password, sendEmail)} disabled={loading || !password} className="w-full bg-amber-500 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-amber-600 transition-colors disabled:opacity-50">
            {loading ? 'Actualizando...' : sendEmail ? 'Cambiar y enviar por mail' : 'Cambiar contraseña'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Cohort Management Modal ──────────────────────────────────
function CohortModal({ user, onClose, onUpdate }: { user: AdminUser; onClose: () => void; onUpdate: () => void }) {
  const [allCohorts, setAllCohorts] = useState<any[]>([]);
  const [userCohortIds, setUserCohortIds] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const token = getToken() || '';
    Promise.all([
      fetchAllCohorts(token),
      fetchUserCohorts(token, user.id),
    ]).then(([all, userC]) => {
      setAllCohorts(all);
      setUserCohortIds(new Set(userC.map((c: Cohort) => c.cohort_id)));
      setLoading(false);
    });
  }, [user.id]);

  const toggle = async (cohortId: number) => {
    setSaving(true);
    const token = getToken() || '';
    const has = userCohortIds.has(cohortId);

    const ok = has
      ? await removeUserCohort(token, user.id, cohortId)
      : await assignUserCohort(token, user.id, cohortId);

    if (ok) {
      setUserCohortIds(prev => {
        const next = new Set(prev);
        has ? next.delete(cohortId) : next.add(cohortId);
        return next;
      });
      onUpdate();
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">
            Camadas — {user.full_name || user.email}
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-3">
          {loading ? (
            <p className="text-sm text-gray-500 text-center py-4">Cargando...</p>
          ) : allCohorts.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">No hay camadas creadas</p>
          ) : (
            allCohorts.map(cohort => (
              <label key={cohort.id} className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={userCohortIds.has(cohort.id)}
                  onChange={() => toggle(cohort.id)}
                  disabled={saving}
                  className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                />
                <div className="flex-1">
                  <span className="text-sm font-medium text-gray-900">{cohort.cohort_name}</span>
                  <span className="text-xs text-gray-500 ml-2">({cohort.cohort_code})</span>
                  <div className="flex gap-3 mt-0.5">
                    <span className="text-[10px] text-gray-400">
                      Teorica: S{cohort.weeks_theoric_unlocked ?? 0}/10
                    </span>
                    <span className="text-[10px] text-gray-400">
                      Grupal: S{cohort.weeks_group_unlocked ?? 0}/10
                    </span>
                    <span className="text-[10px] text-gray-400">
                      {cohort.enrolled_count ?? '?'} alumnos
                    </span>
                  </div>
                </div>
              </label>
            ))
          )}
        </div>

        <div className="px-6 py-3 bg-gray-50 border-t border-gray-100 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

export default UserManagement;
